import React from "react";
import { Box, Select, MenuItem } from "@mui/material";
import { C, Icon, CloseIcon, PeopleIcon } from "./staffingUi";
import { getInstructorName, initials, colorFor } from "./people";
import { LEAD_ROLE, currentSettings, signupSummaryText } from "./staffingModel";

function roleLabel(role) {
  if (role === LEAD_ROLE) return "Lead";
  if (role === "ASSISTANT_INSTRUCTOR") return "Assistant";
  return role;
}

function Avatar({ name }) {
  return (
    <Box sx={{ width: 26, height: 26, borderRadius: "50%", bgcolor: colorFor(name), color: "#fff",
      display: "grid", placeItems: "center", fontSize: "10.5px", fontWeight: 700, flex: "0 0 auto" }}>
      {initials(name)}
    </Box>
  );
}

function IconButton({ label, onClick, children, sx, className }) {
  return (
    <Box component="button" type="button" aria-label={label} title={label} onClick={onClick} className={className}
      sx={{ width: 24, height: 24, border: 0, bgcolor: "transparent", borderRadius: "6px", display: "grid",
        placeItems: "center", color: C.muted, cursor: "pointer", p: 0,
        "&:hover": { bgcolor: C.hover, color: C.ink }, ...sx }}>
      {children}
    </Box>
  );
}

const emptySeat = <Box sx={{ width: 26, height: 26, borderRadius: "50%", border: `1px dashed ${C.lineStrong}`, flex: "0 0 auto" }} />;

export function SlotCell({ opening, instructorMap, candidates, blocked, canEdit, canRemove, assigning,
  onStartAssign, onCancelAssign, onAssign, onUnassign, onRemove }) {
  const filled = opening.status !== "OPEN";
  const name = getInstructorName(instructorMap?.[opening.assignedInstructorId]);
  if (assigning) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minHeight: 28 }}>
        {emptySeat}
        <Select size="small" displayEmpty value="" autoFocus
          inputProps={{ "aria-label": `Assign ${roleLabel(opening.teachingRole)} instructor` }}
          onChange={(e) => e.target.value && onAssign(opening.id, e.target.value)}
          sx={{ height: 30, maxWidth: 200, fontSize: 13 }}>
          <MenuItem value=""><em>Choose instructor…</em></MenuItem>
          {candidates.map((u) => (
            <MenuItem key={u.id} value={u.id} disabled={blocked(u.id)}>
              {getInstructorName(u) || `#${u.id}`}
              {blocked(u.id) && <Box component="span" sx={{ ml: 1, fontSize: 12, color: C.muted }}>no AHA instructor ID</Box>}
            </MenuItem>
          ))}
        </Select>
        <IconButton label="Cancel" onClick={onCancelAssign} sx={{ opacity: 1 }}><CloseIcon /></IconButton>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minHeight: 28, minWidth: 0,
      "&:hover .slot-x, & .slot-x:focus-visible": { opacity: 1 } }}>
      {filled ? (
        <>
          <Avatar name={name || "?"} />
          <Box component="span" sx={{ fontSize: 13.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {name || (opening.assignedInstructorId ? "Unknown" : "Filled")}
          </Box>
          {canEdit && (
            <IconButton label={`Unassign ${name || "instructor"}`} onClick={() => onUnassign(opening.id)}
              sx={{ opacity: 0 }} className="slot-x"><CloseIcon /></IconButton>
          )}
        </>
      ) : (
        <>
          {emptySeat}
          <Box component="span" sx={{ color: C.muted, fontSize: 13.5 }}>Open</Box>
          {canEdit && (
            <Box component="button" type="button" onClick={() => onStartAssign(opening.id)}
              sx={{ border: 0, bgcolor: "transparent", p: "2px 4px", font: "inherit", fontSize: 12.5, fontWeight: 600,
                color: C.violet, cursor: "pointer", borderRadius: "6px", "&:hover": { bgcolor: C.hover } }}>
              Assign
            </Box>
          )}
          {canEdit && canRemove && (
            <IconButton label="Remove opening" onClick={() => onRemove(opening)} sx={{ opacity: 0 }} className="slot-x">
              <CloseIcon />
            </IconButton>
          )}
        </>
      )}
    </Box>
  );
}

/** How the role's open slots are staffed ("Up to 3 sign-ups"), shown only once they are open to instructors. */
export function SignupSummary({ openings }) {
  const settings = currentSettings(openings);
  if (!settings) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "6px", fontSize: 12, color: C.muted, pl: "34px" }}>
      <PeopleIcon />
      {signupSummaryText(settings)}
    </Box>
  );
}

export { Icon, IconButton };
